// Phase 21.5 — Per-row attachment panel on the Print Job detail view.
// Fetches /api/requisitions/{ref}/attachments/manifest and renders one
// download pill per row that has a stored binary. Legacy rows (no
// attachment_id) show a greyed "Legacy — download unavailable" chip.
import { useEffect, useState } from "react";
import { Paperclip, Download, FileText, ExternalLink } from "lucide-react";
import api from "@/lib/api";

function _fmtKB(bytes) {
  if (!bytes) return "—";
  const kb = Math.max(1, Math.round(bytes / 1024));
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function PrintRowsAttachments({ refCode, printRows }) {
  const [manifest, setManifest] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!refCode) return;
    api.get(`/requisitions/${refCode}/attachments/manifest`)
      .then((r) => setManifest(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Unavailable"));
  }, [refCode]);

  // Pre-manifest render: fall back to the inline print_rows metadata so
  // the user still sees "no file" vs "legacy chip" without an API hop.
  const items = manifest?.attachments || [];
  const total = Array.isArray(printRows) ? printRows.length : items.length;

  return (
    <div className="sheet-plain p-4 sm:p-5 mb-6" data-testid="print-rows-attachments">
      <div className="flex items-center gap-2 mb-3">
        <Paperclip size={16} className="text-[#7a1f2b]" />
        <h3 className="font-serif text-lg text-[#1f2320]">Row attachments</h3>
        <span className="text-xs text-[#6e7770] ml-1">
          ({items.length} of {total} rows have a file)
        </span>
      </div>

      {err && (
        <div className="text-sm text-[#b3261e]" data-testid="pra-error">
          Could not load attachments: {err}
        </div>
      )}

      {!err && items.length === 0 && (
        <div className="text-sm text-[#6e7770]" data-testid="pra-empty">
          No downloadable attachments on this record.
        </div>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-[#eadfca]" data-testid="pra-list">
          {items.map((a) => (
            <li key={a.row_index}
                className="py-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="shrink-0 inline-flex w-7 h-7 items-center
                                 justify-center rounded-full bg-[#faf5eb]
                                 text-[#7a1f2b] text-xs font-semibold">
                  {a.sl_no || (a.row_index + 1)}
                </span>
                <div className="min-w-0">
                  <div className="text-sm text-[#1f2320] truncate">
                    {a.file_name || a.filename || "—"}
                  </div>
                  <div className="text-[11px] text-[#6e7770]">
                    <FileText size={11} className="inline mr-1 align-text-top" />
                    {a.filename || "attachment.pdf"} · {_fmtKB(a.size_bytes)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {a.has_binary ? (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${a.download_url}`}
                     className="inline-flex items-center gap-1.5 px-3 py-1.5
                                rounded-md border border-[#2d6b6b]
                                text-[#2d6b6b] hover:bg-[#2d6b6b]
                                hover:text-white transition-colors
                                text-xs font-semibold"
                     data-testid={`print-row-${a.row_index}-download`}
                     aria-label={`Download ${a.filename}`}>
                    <Download size={13} /> Download
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1
                                   rounded-md bg-[#f1ebdc] text-[#6e7770]
                                   text-[11px]"
                        data-testid={`print-row-${a.row_index}-legacy`}>
                    Legacy — download unavailable
                  </span>
                )}
                {a.drive_web_link && (
                  <a href={a.drive_web_link}
                     target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 px-2 py-1
                                rounded-md text-[#2d6b6b] hover:bg-[#eef5f5]
                                text-[11px]"
                     data-testid={`print-row-${a.row_index}-drive`}
                     title="Open in Drive">
                    <ExternalLink size={11} /> Drive
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
